"use client";

import { AnimatePresence, motion } from "motion/react";
import { X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";
import { cn } from "../lib/utils";

interface ModalProperties {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
  mobileOptimized?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  mobileOptimized = true,
}: ModalProperties) {
  const modalReference = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Handle body scroll lock and focus management
  useEffect(() => {
    if (isOpen) {
      // Store currently focused element to restore later
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";

      // Focus the modal container
      setTimeout(() => modalReference.current?.focus(), 0);
    } else {
      document.body.style.overflow = "";

      // Restore focus to previously focused element
      previouslyFocusedElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap - keep focus within modal
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== "Tab" || !modalReference.current) return;

    const focusableElements =
      modalReference.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
    const firstElement = focusableElements[0];
    // eslint-disable-next-line unicorn/prefer-at
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
    }
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            style={{ willChange: "opacity" }}
            aria-hidden="true"
          />
          {/* Modal */}
          <div
            className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
          >
            <motion.div
              ref={modalReference}
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              initial={{ opacity: 0, y: mobileOptimized ? "100%" : 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: mobileOptimized ? "100%" : 20 }}
              transition={{
                type: "spring",
                damping: 30,
                stiffness: 400,
              }}
              className={cn(
                "relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl outline-none",
                mobileOptimized && "max-h-[90vh] overflow-y-auto",
                className,
              )}
              onClick={(event) => event.stopPropagation()}
              style={{ willChange: "transform, opacity" }}
            >
              {/* Header */}
              {(title || mobileOptimized) && (
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4 rounded-t-2xl">
                  {title && (
                    <h2
                      id={titleId}
                      className="text-lg font-semibold text-boldness sm:text-xl uppercase"
                    >
                      {title}
                    </h2>
                  )}
                  <button
                    onClick={onClose}
                    className="ml-auto rounded-full p-2 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Sluiten"
                  >
                    <X className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  </button>
                </div>
              )}
              {/* Content */}
              <div className="p-4 sm:p-6">{children}</div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
